import { Component, computed, effect, NgZone, ViewChild, ElementRef, Output, EventEmitter, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { GenetricDetailComponent } from '@app/common-codes/components/edit-components/generic-detail.component';
import { OnReadyDirective } from '@app/common-codes/components/directives/OnReadyDirective';
import { FieldContextDirective } from '@app/common-codes/components/directives/FieldContext.directive';
import { TranslatePipe } from '@app/common-codes/components/directives/TranslatePipe';
import { Register } from '@app/common-codes/decorators/DependencyInjectionRegistreyDecorators';
import { AlertModalService } from '@app/common-codes/services/AlertModalService';
import { GenericValidationService } from '@app/common-codes/services/GenericValidationService';
import { TranslationService } from '@app/common-codes/services/TranslationService';
import { MatwinsSkins } from '@app/common-codes/skins/global.skin';
import { IStaticStylesConfig } from '@app/common-codes/skins/_primitives/IStaticStylesConfig';
import { PointOfService } from '@app/common-codes/models/matwins-tenant/PointOfService.model';
import { CurrentTenantService } from '@app/common-codes/services/CurrentTenantService';
import { MatwinsVariablesService } from '@app/common-codes/services/matwinsVariables.service';
import { QrcodeService } from '@app/common-codes/services/qrcode.service';
import { LookupItem } from '@app/common-codes/services/LookupService';
import { HtmlEditorComponent } from '@app/common-codes/components/nested-components/html-editor/html-editor.component';
import { FieldWrapperComponent } from '@app/common-codes/components/nested-components/field-wrapper/field-wrapper.component';
import { Policy } from '@app/common-codes/models/matwins-tenant/Policy.model';

import { TranslatableEntityModalComponent } from '@app/common-codes/components/nested-components/translatable-entity-modal/translatable-entity-modal.component';
import { TranslatableEntityConfig, TranslatableItem, TranslatableModalSaveEvent } from '@app/common-codes/components/nested-components/translatable-entity-modal/translatable-entity.interface';
import { Translation, TranslationRelationType } from '@app/common-codes/models/matwins-tenant/navigation-entities/Translation.model';
import { Engagement } from '@app/common-codes/models/matwins-tenant/Engagement.model';
import { Quiz } from '@app/common-codes/models/matwins-tenant/Quiz.model';
import { Covid } from '@app/common-codes/models/matwins-tenant/Covid.model';
import { BuzzerConfig } from '@app/common-codes/models/matwins-tenant/BuzzerConfig.model';
import { QuizResponse } from '@app/common-codes/models/matwins-tenant/QuizResponse.model';

interface PendingPolicyFile {
  id: number;
  file: File;
  policyType: number;
  languageId?: number;
  languageKey?: string;
}

@Register(PointOfService)
@Component({
  selector: 'app-pos-detail',
  templateUrl: './pos-detail.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, FormsModule, TranslatePipe, OnReadyDirective, HtmlEditorComponent, TranslatableEntityModalComponent, FieldWrapperComponent, FieldContextDirective]
})
export class PointOfServiceDetailComponent extends GenetricDetailComponent<PointOfService> implements OnInit {
  override ComponentEntityClass = PointOfService;
  Skin = MatwinsSkins.V1 as IStaticStylesConfig;
  // @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Output() itemSaved = new EventEmitter<PointOfService>();

  @ViewChild('qrCodeTruckerCheckinContainer') qrCodeTruckerCheckinContainer!: ElementRef;

  baseUrl = '';

  previousPosId: number | null = null;
  previousClientId: string | null = null;

  languageLookup = signal<LookupItem[]>([]);
  smsProviderLookup = signal<LookupItem[]>([]);
  smsProviderOptions = computed(() =>
    [...this.smsProviderLookup()].sort((a, b) => Number(a.key) - Number(b.key))
  );

  // Fuseaux horaires : la clé correspond à la valeur de l'enum TimeZone côté backend (1..139)
  readonly timeZoneOptions: { key: number; label: string }[] = [
    '(UTC-12:00) International Date Line West',
    '(UTC-11:00) Samoa',
    '(UTC-11:00) Coordinated Universal Time-11',
    '(UTC-10:00) Aleutian Islands',
    '(UTC-10:00) Hawaii',
    '(UTC-09:30) Marquesas Islands',
    '(UTC-09:00) Alaska',
    '(UTC-09:00) Coordinated Universal Time-09',
    '(UTC-07:00) Yukon',
    '(UTC-08:00) Baja California',
    '(UTC-08:00) Coordinated Universal Time-08',
    '(UTC-08:00) Pacific Time (US Canada)',
    '(UTC-07:00) Arizona',
    '(UTC-07:00) Chihuahua, La Paz, Mazatlan',
    '(UTC-07:00) Mountain Time (US Canada)',
    '(UTC-05:00) Chetumal',
    '(UTC-06:00) Central America',
    '(UTC-06:00) Central Time (US Canada)',
    '(UTC-06:00) Easter Island',
    '(UTC-06:00) Guadalajara, Mexico City, Monterrey',
    '(UTC-06:00) Saskatchewan',
    '(UTC-05:00) Bogota, Lima, Quito, Rio Branco',
    '(UTC-05:00) Eastern Time (US Canada)',
    '(UTC-05:00) Haiti',
    '(UTC-05:00) Havana',
    '(UTC-05:00) Indiana (East)',
    '(UTC-05:00) Turks and Caicos',
    '(UTC-04:00) Caracas',
    '(UTC-03:00) Punta Arenas',
    '(UTC-04:00) Asuncion',
    '(UTC-04:00) Atlantic Time (Canada)',
    '(UTC-04:00) Cuiaba',
    '(UTC-04:00) Georgetown, La Paz, Manaus, San Juan',
    '(UTC-04:00) Santiago',
    '(UTC-03:30) Newfoundland',
    '(UTC-03:00) Araguaina',
    '(UTC-03:00) Brasilia',
    '(UTC-03:00) Cayenne, Fortaleza',
    '(UTC-03:00) City of Buenos Aires',
    '(UTC-03:00) Greenland',
    '(UTC-03:00) Montevideo',
    '(UTC-03:00) Saint Pierre and Miquelon',
    '(UTC-03:00) Salvador',
    '(UTC-02:00) Coordinated Universal Time-02',
    '(UTC-02:00) Mid-Atlantic - Old',
    '(UTC-01:00) Azores',
    '(UTC-01:00) Cabo Verde Is.',
    '(UTC) Coordinated Universal Time',
    '(UTC+00:00) Dublin, Edinburgh, Lisbon, London',
    '(UTC+00:00) Monrovia, Reykjavik',
    '(UTC+01:00) Casablanca',
    '(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
    '(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague',
    '(UTC+01:00) Brussels, Copenhagen, Madrid, Paris',
    '(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb',
    '(UTC+01:00) West Central Africa',
    '(UTC+02:00) Tripoli',
    '(UTC+02:00) Windhoek',
    '(UTC+02:00) Amman',
    '(UTC+02:00) Athens, Bucharest',
    '(UTC+02:00) Beirut',
    '(UTC+02:00) Cairo',
    '(UTC+02:00) Chisinau',
    '(UTC+02:00) Damascus',
    '(UTC+02:00) Gaza, Hebron',
    '(UTC+02:00) Harare, Pretoria',
    '(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
    '(UTC+02:00) Jerusalem',
    '(UTC+02:00) Kaliningrad',
    '(UTC+02:00) Khartoum',
    '(UTC+03:00) Istanbul',
    '(UTC+03:00) Minsk',
    '(UTC+03:00) Baghdad',
    '(UTC+03:00) Kuwait, Riyadh',
    '(UTC+03:00) Moscow, St. Petersburg',
    '(UTC+03:00) Nairobi',
    '(UTC+04:00) Astrakhan, Ulyanovsk',
    '(UTC+04:00) Izhevsk, Samara',
    '(UTC+04:00) Saratov',
    '(UTC+04:00) Volgograd',
    '(UTC+03:30) Tehran',
    '(UTC+04:00) Abu Dhabi, Muscat',
    '(UTC+04:00) Baku',
    '(UTC+04:00) Port Louis',
    '(UTC+04:00) Tbilisi',
    '(UTC+04:00) Yerevan',
    '(UTC+04:30) Kabul',
    '(UTC+05:00) Ashgabat, Toshkent',
    '(UTC+05:00) Ekaterinburg',
    '(UTC+05:00) Islamabad, Karachi',
    '(UTC+05:00) Qyzylorda',
    '(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi',
    '(UTC+05:30) Sri Jayawardenepura',
    '(UTC+05:45) Kathmandu',
    '(UTC+06:00) Astana',
    '(UTC+06:00) Dhaka',
    '(UTC+06:00) Omsk',
    '(UTC+07:00) Barnaul, Gorno-Altaysk',
    '(UTC+07:00) Novosibirsk',
    '(UTC+07:00) Tomsk',
    '(UTC+06:30) Yangon (Rangoon)',
    '(UTC+07:00) Bangkok, Hanoi, Jakarta',
    '(UTC+07:00) Hovd',
    '(UTC+07:00) Krasnoyarsk',
    '(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi',
    '(UTC+08:00) Irkutsk',
    '(UTC+08:00) Kuala Lumpur, Singapore',
    '(UTC+08:00) Perth',
    '(UTC+08:00) Taipei',
    '(UTC+08:00) Ulaanbaatar',
    '(UTC+09:00) Chita',
    '(UTC+09:00) Pyongyang',
    '(UTC+08:45) Eucla',
    '(UTC+09:00) Osaka, Sapporo, Tokyo',
    '(UTC+09:00) Seoul',
    '(UTC+09:00) Yakutsk',
    '(UTC+09:30) Adelaide',
    '(UTC+09:30) Darwin',
    '(UTC+10:00) Brisbane',
    '(UTC+10:00) Canberra, Melbourne, Sydney',
    '(UTC+10:00) Guam, Port Moresby',
    '(UTC+10:00) Hobart',
    '(UTC+10:00) Vladivostok',
    '(UTC+11:00) Bougainville Island',
    '(UTC+11:00) Magadan',
    '(UTC+11:00) Sakhalin',
    '(UTC+10:30) Lord Howe Island',
    '(UTC+11:00) Chokurdakh',
    '(UTC+11:00) Norfolk Island',
    '(UTC+11:00) Solomon Is., New Caledonia',
    '(UTC+12:00) Anadyr, Petropavlovsk-Kamchatsky',
    '(UTC+12:00) Auckland, Wellington',
    '(UTC+12:00) Coordinated Universal Time+12',
    '(UTC+12:00) Fiji',
    '(UTC+12:00) Petropavlovsk-Kamchatsky - Old',
    '(UTC+12:45) Chatham Islands',
    '(UTC+13:00) Coordinated Universal Time+13',
    "(UTC+13:00) Nuku'alofa",
    '(UTC+14:00) Kiritimati Island'
  ].map((label, i) => ({ key: i + 1, label }));

  // Policy upload modal state
  activePolicyTab = signal<string>('html');
  pendingPolicyFiles = signal<PendingPolicyFile[]>([]);
  private pendingFileCounter = 0;

  override imageRelationName = 'FK_Image_Logo';

  // =============================================
  // Translatable Entity Modal — configs & state
  // =============================================

  activeTranslatableModal = signal<string | null>(null); // 'engagement' | 'quiz' | 'covid' | 'buzzer' | 'firealert' | null

  get engagementConfig(): TranslatableEntityConfig {
    return {
      modalTitle: 'vues.translatableEntityModal.engagements.title',
      modalIcon: 'fas fa-handshake',
      relationType: TranslationRelationType.Engagement,
      translationFields: [
        { type: 1, label: 'vues.translatableEntityModal.fields.title', inputType: 'text' },
        { type: 2, label: 'vues.translatableEntityModal.fields.description', inputType: 'textarea' }
      ],
      addItemLabel: 'vues.translatableEntityModal.engagements.addItem',
      showRightResponse: false
    };
  }

  get quizTranslationConfig(): TranslatableEntityConfig {
    return {
      modalTitle: 'vues.translatableEntityModal.quiz.title',
      modalIcon: 'fas fa-question-circle',
      relationType: TranslationRelationType.Quiz,
      translationFields: [
        { type: 1, label: 'vues.translatableEntityModal.fields.question', inputType: 'text' },
        { type: 2, label: 'vues.translatableEntityModal.fields.description', inputType: 'textarea' }
      ],
      addItemLabel: 'vues.translatableEntityModal.quiz.addItem',
      showRightResponse: false,
      childConfig: {
        entityLabel: 'vues.translatableEntityModal.quiz.answers',
        addLabel: 'vues.translatableEntityModal.quiz.addAnswer',
        translationFields: [
          { type: 4, label: 'vues.translatableEntityModal.fields.answer', inputType: 'text' }
        ],
        showRightResponse: true
      }
    };
  }

  get covidTranslationConfig(): TranslatableEntityConfig {
    return {
      modalTitle: 'vues.translatableEntityModal.health.title',
      modalIcon: 'fas fa-file-medical',
      relationType: TranslationRelationType.Covid,
      translationFields: [
        { type: 1, label: 'vues.translatableEntityModal.fields.question', inputType: 'text' },
        { type: 2, label: 'vues.translatableEntityModal.fields.description', inputType: 'textarea' }
      ],
      addItemLabel: 'vues.translatableEntityModal.health.addItem',
      showRightResponse: false,
      extraFields: [
        { property: 'notificationExclusion', label: 'vues.translatableEntityModal.health.exclusionNotifications', inputType: 'checkbox', defaultValue: false },
        { property: 'fK_Parent', label: 'vues.translatableEntityModal.health.dependsOn', inputType: 'select', selectSource: 'self', defaultValue: null },
        { property: 'parentValue', label: 'vues.translatableEntityModal.health.dependentAnswer', inputType: 'radio-bool', defaultValue: null }
      ]
    };
  }

  get buzzerTranslationConfig(): TranslatableEntityConfig {
    return {
      modalTitle: 'vues.translatableEntityModal.buzzer.title',
      modalIcon: 'fas fa-bell',
      relationType: TranslationRelationType.BuzzerTitle,
      translationFields: [
        { type: 1, label: 'vues.translatableEntityModal.fields.title', inputType: 'text' },
        { type: 2, label: 'vues.translatableEntityModal.fields.description', inputType: 'textarea' }
      ],
      addItemLabel: 'vues.translatableEntityModal.buzzer.addItem',
      showRightResponse: false
    };
  }

  get fireAlertTranslationConfig(): TranslatableEntityConfig {
    return {
      modalTitle: 'vues.pointOfService.detail.sections.evacuationAlert',
      modalIcon: 'fas fa-running',
      relationType: TranslationRelationType.FireAlert,
      translationFields: [
        { type: 1, label: 'vues.translatableEntityModal.fireAlert.emailSubject', inputType: 'text' },
        { type: 2, label: 'vues.translatableEntityModal.fireAlert.emailBody', inputType: 'textarea' },
        { type: 5, label: 'vues.translatableEntityModal.fireAlert.sms', inputType: 'text' },
        { type: 6, label: 'vues.translatableEntityModal.fireAlert.teams', inputType: 'text' }
      ],
      addItemLabel: 'vues.translatableEntityModal.fireAlert.addItem',
      showRightResponse: false,
      singleItem: true,
      hideDescription: true
    };
  }

  // --- Getters pour chaque entité ---

  getEngagements(): TranslatableItem[] {
    return (this.editingEntity()?.engagements || []) as TranslatableItem[];
  }

  getQuizItems(): TranslatableItem[] {
    return (this.editingEntity()?.quizzes || []).map(quiz => ({
      ...quiz,
      children: (quiz.quizResponses || []).map(qr => ({
        id: qr.id,
        description: qr.description || '',
        numero: qr.numero,
        translations: qr.translations || [],
        isRightResponse: qr.isRightResponse
      }))
    })) as TranslatableItem[];
  }

  getCovidItems(): TranslatableItem[] {
    return (this.editingEntity()?.covids || []) as TranslatableItem[];
  }

  getBuzzerItems(): TranslatableItem[] {
    return (this.editingEntity()?.buzzerConfigs || []) as TranslatableItem[];
  }

  getFireAlertItems(): TranslatableItem[] {
    const pos = this.editingEntity();
    if (!pos) return [];
    return [{
      id: pos.id,
      description: pos.name ?? '',
      translations: pos.fireAlertTranslations || []
    }];
  }

  // Texte d'alerte dans la langue par défaut du site (aperçu dans la carte)
  getFireAlertText(type: number): string {
    const pos = this.editingEntity();
    if (!pos) return '—';
    const translated = (pos.fireAlertTranslations || [])
      .find(t => t.typeDeTraduction === type && t.fK_Language === pos.fK_i18nLanguage)
      ?.translationValue;
    if (translated) return translated;
    if (type === 1) return pos.fireAlert_Email_Subject || '—';
    if (type === 2) return pos.fireAlert_Email_Body || '—';
    if (type === 5) return pos.fireAlert_SMS || '—';
    return '—';
  }

  getEngagementRdlcPolicies(): Policy[] {
    return (this.editingEntity()?.policies || [])
      .filter(p => p.policyType === 6)
      .sort((a, b) => (b.numero || 0) - (a.numero || 0));
  }

  // --- Getter générique pour les items selon le type ---

  getTranslatableItems(type: string): TranslatableItem[] {
    switch (type) {
      case 'engagement': return this.getEngagements();
      case 'quiz': return this.getQuizItems();
      case 'covid': return this.getCovidItems();
      case 'buzzer': return this.getBuzzerItems();
      case 'firealert': return this.getFireAlertItems();
      default: return [];
    }
  }

  getTranslatableConfig(type: string): TranslatableEntityConfig {
    switch (type) {
      case 'engagement': return this.engagementConfig;
      case 'quiz': return this.quizTranslationConfig;
      case 'covid': return this.covidTranslationConfig;
      case 'buzzer': return this.buzzerTranslationConfig;
      case 'firealert': return this.fireAlertTranslationConfig;
      default: return this.engagementConfig;
    }
  }

  getRdlcPoliciesForType(type: string): Policy[] {
    switch (type) {
      case 'engagement': return this.getEngagementRdlcPolicies();
      default: return [];
    }
  }

  constructor(
    route: ActivatedRoute,
    router: Router,
    translationService: TranslationService,
    validationService: GenericValidationService,
    alertModalService: AlertModalService,
    ngZone: NgZone,
    matwinsVariables: MatwinsVariablesService,
    private currentTenantService: CurrentTenantService,
    private qrCodeService: QrcodeService
  ) {
    super(route, router, translationService, validationService, ngZone);
    this.alertModalService = alertModalService;

    effect(() => {
      const pos = this.editingEntity();
      if (pos) {
        const tenant = this.currentTenantService.currentTenant();
        if (tenant) {
          // Lire la revision : si les listes arrivent en retard, l'effet repasse et les remplit.
          this.lookupService.revision();
          this.languageLookup.set(this.getLookupData('i18nLanguage'));
          this.smsProviderLookup.set(this.getLookupData('SmsProvider'));

          // Site jamais configure : on propose le fournisseur marque par defaut.
          if (!pos.smsProvider) {
            const defaut = this.smsProviderLookup().find(p => p.additionalData?.['isdefault'] === true);
            if (defaut) {
              pos.smsProvider = Number(defaut.key);
            }
          }

          pos.sourceTenantDTO = structuredClone(tenant);
          pos.sourceTenantDTO.pointOfServices = [];                   // Ici [] pour eviter les redondances cycliques 
          pos.fK_Tenant = tenant.id;
          if (this.previousClientId != tenant.managerClient
            || this.previousPosId != pos.id) {

            this.baseUrl = `${window.location.origin}/qrcode/${tenant.managerClient}/${pos.id}`;
            if (this.generateQrCode()) {
              this.previousClientId = tenant.managerClient;
              this.previousPosId = pos.id;
            }
          }

          this.imageUrl = pos?.imageLogo?.rawData_Path ? '/Documents/' + pos.imageLogo.rawData_Path : null;

        }
      }
    });
  }

  override getEmptyEntity(): PointOfService {
    return new PointOfService();
  }

  override ngOnInit(): void {
    super.ngOnInit?.();

  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    document.querySelectorAll('.open-modal').forEach(button => {
      button.addEventListener('click', () => {
        const modalId = button.getAttribute('data-modal');
        if (modalId) {
          this.openModal(modalId);
        }
      });
    });

    document.querySelectorAll('.close-modal').forEach(button => {
      button.addEventListener('click', () => {
        const modal = button.closest('.fixed');
        if (modal) {
          this.hideModal(modal.id);
        }
      });
    });
  }

  override bindingHandler(el: HTMLElement, field: string): void {
    //const input = el as HTMLInputElement;
    //let model = this.editingEntity();
    super.bindingHandler(el, field); // autres champs
  }

  // Masque l'heure de checkout automatique quand le mode est "None".
  get isAutomaticCheckoutEnabled(): boolean {
    const mode = this.editingEntity()?.automaticCheckOut;
    return mode !== null && mode !== undefined && Number(mode) !== 0;
  }

  // Réinitialise l'heure quand on repasse le mode à "None".
  onAutomaticCheckOutChange(event: Event): void {
    if ((event.target as HTMLSelectElement).value === '0')
      this.updateEditingEntity('automaticCheckOutHour', null);
  }

  // L'heure est obligatoire (metadata Required) seulement quand le mode != "None".
  override validateProperty(propertyName: string, value: any): void {
    if (propertyName === 'automaticCheckOutHour' && !this.isAutomaticCheckoutEnabled) {
      this.validationErrors.update(errors => ({ ...errors, [propertyName]: [] }));
      return;
    }
    super.validateProperty(propertyName, value);
  }

  override onEscapeKeydown(event: Event): void {
    if (this.activeTranslatableModal()) {
      this.closeTranslatableModal();
      event.preventDefault();
      return;
    }
    super.onEscapeKeydown(event);
  }


  generateQrCode(): boolean {
    let generated = false;

    let urlHome = `${this.baseUrl}/home`;
    const homeContainers = document.querySelectorAll('.qrCodeHomeContainer');
    homeContainers.forEach(homeContainer => {
      homeContainer.innerHTML = ''; // Vider la div avant d'ajouter le QR
      this.qrCodeService.appendToElement(homeContainer as HTMLElement, urlHome);
      generated = true;
    });

    let urlCheckIn = `${this.baseUrl}/trucker-checkin`;
    const checkinContainer = document.getElementById('qrCodeTruckerCheckinContainer');
    if (checkinContainer) {
      checkinContainer.innerHTML = ''; // Vider la div avant d'ajouter le QR
      this.qrCodeService.appendToElement(checkinContainer, urlCheckIn);
      generated = true;
    }

    let urlCheckout = `${this.baseUrl}/trucker-checkout`;
    const checkoutContainer = document.getElementById('qrCodeTruckerCheckoutContainer');
    if (checkoutContainer) {
      checkoutContainer.innerHTML = ''; // Vider la div avant d'ajouter le QR
      this.qrCodeService.appendToElement(checkoutContainer, urlCheckout);
      generated = true;
    }

    return generated;
  }



  override parseToEntityInstance(): void {
    if (this.EntityInstance && this.editingEntity()) {
      Object.assign(this.EntityInstance, this.editingEntity());
    }
  }

  // --- Policy helpers ---

  extractLanguageFromFilename(filename: string): LookupItem | null {
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const lastUnderscore = nameWithoutExt.lastIndexOf('_');
    if (lastUnderscore === -1) return null;
    const suffix = nameWithoutExt.substring(lastUnderscore + 1).toLowerCase();
    return this.languageLookup().find(l => l.value.toLowerCase() === suffix) || null;
  }

  getVisitorHtmlPolicies(): Policy[] {
    return (this.editingEntity()?.policies || [])
      .filter(p => p.policyType === 3)
      .sort((a, b) => (b.numero || 0) - (a.numero || 0));
  }

  getVisitorPdfPoliciesByLanguage(langKey: string): Policy[] {
    return (this.editingEntity()?.policies || [])
      .filter(p => {
        if (p.policyType !== 1) return false;
        const filename = p.file?.fileName || p.description || '';
        const suffix = this.extractSuffixFromFilename(filename);
        return suffix === langKey.toLowerCase();
      })
      .sort((a, b) => (b.numero || 0) - (a.numero || 0));
  }

  private extractSuffixFromFilename(filename: string): string | null {
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const lastUnderscore = nameWithoutExt.lastIndexOf('_');
    if (lastUnderscore === -1) return null;
    return nameWithoutExt.substring(lastUnderscore + 1).toLowerCase();
  }

  getPendingFiles(policyType: number, languageId?: number): PendingPolicyFile[] {
    return this.pendingPolicyFiles().filter(f =>
      f.policyType === policyType &&
      (languageId != null ? f.languageId === languageId : f.languageId == null)
    );
  }

  onPolicyFileSelected(event: Event, policyType: number, expectedLangId?: number): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;
    const file = input.files[0];

    if (policyType === 1) {
      // PDF — la langue vient de l'onglet actif
      const activeTab = this.activePolicyTab();
      const lang = this.languageLookup().find(l => l.id === activeTab);
      this.pendingPolicyFiles.update(files => [
        ...files,
        { id: ++this.pendingFileCounter, file, policyType, languageId: lang ? +lang.id : expectedLangId, languageKey: lang?.value }
      ]);
    } else {
      // RDLC
      this.pendingPolicyFiles.update(files => [
        ...files,
        { id: ++this.pendingFileCounter, file, policyType }
      ]);
    }
    input.value = '';
  }

  removePendingFile(fileId: number): void {
    this.pendingPolicyFiles.update(files => files.filter(f => f.id !== fileId));
  }

  private async uploadPendingPolicyFiles(): Promise<void> {
    const files = this.pendingPolicyFiles();
    if (files.length === 0) return;

    const posId = this.editingEntity()?.id;
    if (!posId) return;

    for (const pending of files) {
      const formData = new FormData();

      // Suffixer le nom du fichier avec la langue si c'est un PDF
      if (pending.policyType === 1 && pending.languageKey) {
        const ext = pending.file.name.substring(pending.file.name.lastIndexOf('.'));
        const nameWithoutExt = pending.file.name.substring(0, pending.file.name.lastIndexOf('.'));
        const newName = `${nameWithoutExt}_${pending.languageKey}${ext}`;
        formData.append('file', pending.file, newName);
      } else {
        formData.append('file', pending.file);
      }

      formData.append('posId', posId.toString());
      formData.append('policyType', pending.policyType.toString());
      await this.dataService.execute<any>('Post', 'upload-policy', formData);
    }

    this.pendingPolicyFiles.set([]);
    this.pendingFileCounter = 0;
    await this.loadData(posId);
  }

  async deletePolicy(policyId: number): Promise<void> {
    const confirm = await this.alertModalService?.showAlert({
      title: this.translationService.instant('buttons.delete'),
      message: this.translationService.instant('vues.alert-modals.confirm-delete.message'),
      yesButtonText: this.translationService.instant('buttons.yes'),
      noButtonText: this.translationService.instant('buttons.no'),
      Skin: this.Skin
    });
    if (!confirm) return;

    try {
      await this.dataService.execute<any>('Delete', `delete-policy/${policyId}`, null);

      // Mettre à jour l'entité locale pour retirer la policy supprimée
      const pos = this.editingEntity();
      if (pos) {
        const updated = Object.assign(new PointOfService(), pos);
        updated.policies = (pos.policies || []).filter(p => p.id !== policyId);
        this.editingEntity.set(updated);
      }
    } catch (error: any) {
      await this.alertModalService?.showAlert({
        title: 'Error',
        message: 'This policy is used by visits and cannot be deleted.',
        Skin: this.Skin
      });
    }
  }

  // --- Save overrides (upload policy files after POS save) ---

  async saveItem(): Promise<void> {
    //this.isNew.set(true);                         // Attention : Ligne a retirer  
    const savedItem = await this.save();
    if (savedItem) {
      await this.uploadPendingPolicyFiles();
      this.itemSaved.emit(savedItem);
    }
  }

  async saveAndCloseItem(): Promise<void> {
    const savedItem = await this.save();
    if (savedItem) {
      await this.uploadPendingPolicyFiles();
      this.itemSaved.emit(savedItem);
      this.closeModal.emit();
    }
  }

  // --- QR Code downloads ---

  downloadHome() {
    let url = window.location.origin + '/qrcode/home';

    this.qrCodeService.download(url, 'home', 'png', {
      backgroundOptions: {
        color: '#ffffff'
      }
    });
  }

  downloadTruckerCheckin() {
    let url = window.location.origin + '/qrcode/trucker-checkin';

    this.qrCodeService.download(url, 'trucker-checkin', 'png', {
      backgroundOptions: {
        color: '#ffffff'
      }
    });
  }
  downloadTruckerCheckout() {
    let url = window.location.origin + '/qrcode/trucker-checkout';

    this.qrCodeService.download(url, 'trucker-checkout', 'png', {
      backgroundOptions: {
        color: '#ffffff'
      }
    });
  }

  // --- Modal open/close ---

  openTranslatableModal(type: string): void {
    this.activeTranslatableModal.set(type);
  }

  closeTranslatableModal(): void {
    this.activeTranslatableModal.set(null);
  }

  // --- Save handler (updates local entity, persisted with POS save) ---

  onTranslatableSave(type: string, event: TranslatableModalSaveEvent): void {
    const pos = this.editingEntity();
    if (!pos) return;

    // L'alerte d'évacuation n'est pas une collection d'entités : ses
    // traductions sont rattachées au site (FIREALERT_{posId}).
    if (type === 'firealert') {
      const updated = Object.assign(new PointOfService(), pos);
      updated.fireAlertTranslations = event.translations;

      // Les champs FireAlert_* reflètent la langue par défaut du site
      const defaultLanguageId = pos.fK_i18nLanguage;
      const textFor = (t: number) => event.translations
        .find(tr => tr.typeDeTraduction === t && tr.fK_Language === defaultLanguageId)?.translationValue;
      updated.fireAlert_Email_Subject = textFor(1) ?? pos.fireAlert_Email_Subject;
      updated.fireAlert_Email_Body = textFor(2) ?? pos.fireAlert_Email_Body;
      updated.fireAlert_SMS = textFor(5) ?? pos.fireAlert_SMS;

      this.editingEntity.set(updated);
      this.closeTranslatableModal();
      return;
    }

    const config = this.getTranslatableConfig(type);

    // Construire les entités avec leurs traductions
    const updatedItems = event.items.map(item => {
      // Créer l'entité appropriée selon le type
      let entity: any;

      switch (type) {
        case 'quiz': {
          const quiz = new Quiz();
          quiz.id = item.id;
          quiz.description = item.description;
          quiz.numero = item.numero;
          quiz.fK_PointOfService = pos.id;
          // Construire les QuizResponse enfants
          quiz.quizResponses = (item.children || []).map(child => {
            const qr = new QuizResponse();
            qr.id = child.id;
            qr.fK_Quiz = item.id;
            qr.isRightResponse = child['isRightResponse'] ?? false;
            qr.description = child.description;
            qr.numero = child.numero;
            qr.translations = event.translations
              .filter(t => t.id === Translation.buildId(TranslationRelationType.QuizResponse as any, child.id))
              .map(t => {
                const tr = new Translation();
                tr.id = t.id;
                tr.fK_Language = t.fK_Language;
                tr.typeDeTraduction = t.typeDeTraduction;
                tr.translationValue = t.translationValue;
                // if (t.rightResponse != null) tr.rightResponse = t.rightResponse;  // SUPPRIMER                
                return tr;
              });
            return qr;
          });
          entity = quiz;
          break;
        }
        case 'covid': {
          const covid = new Covid();
          covid.id = item.id;
          covid.description = item.description;
          covid.numero = item.numero;
          covid.fK_PointOfService = pos.id;
          covid.notificationExclusion = item['notificationExclusion'] ?? false;
          covid.fK_Parent = item['fK_Parent'] ?? null;
          covid.parentValue = item['parentValue'] ?? null;
          entity = covid;
          break;
        }
        case 'buzzer': {
          const buzzer = new BuzzerConfig();
          buzzer.id = item.id;
          buzzer.numero = item.numero;
          buzzer.fK_PointOfService = pos.id;
          buzzer.show = item['show'] ?? true;
          buzzer.status = item['status'] ?? 1;
          buzzer.fK_Employee = item['fK_Employee'] ?? null;
          entity = buzzer;
          break;
        }
        default: {
          const engagement = new Engagement();
          engagement.id = item.id;
          engagement.Description = item.description;
          engagement.Numero = item.numero;
          engagement.fK_PointOfService = pos.id;
          entity = engagement;
          break;
        }
      }

      // Attacher les traductions filtrées pour cet item
      entity.translations = event.translations
        .filter(t => t.id === Translation.buildId(config.relationType as any, item.id))
        .map(t => {
          const tr = new Translation();
          tr.id = t.id;
          tr.fK_Language = t.fK_Language;
          tr.typeDeTraduction = t.typeDeTraduction;
          tr.translationValue = t.translationValue;
          // if (t.rightResponse != null) tr.rightResponse = t.rightResponse;
          return tr;
        });

      return entity;
    });

    // Mettre à jour l'entité locale
    const updated = Object.assign(new PointOfService(), pos);

    switch (type) {
      case 'engagement':
        updated.engagements = updatedItems;
        break;
      case 'quiz':
        updated.quizzes = updatedItems;
        break;
      case 'covid':
        updated.covids = updatedItems;
        break;
      case 'buzzer':
        updated.buzzerConfigs = updatedItems;
        break;
    }

    this.editingEntity.set(updated);
    this.closeTranslatableModal();
  }
  onTranslatableItemDelete(itemId: number): void {
    const pos = this.editingEntity();
    if (!pos) return;

    const type = this.activeTranslatableModal();
    if (!type) return;

    const updated = Object.assign(new PointOfService(), pos);

    switch (type) {
      case 'engagement':
        updated.engagements = (pos.engagements || []).filter(e => e.id !== itemId);
        break;
      case 'quiz':
        updated.quizzes = (pos.quizzes || []).filter(q => q.id !== itemId);
        break;
      case 'covid':
        updated.covids = (pos.covids || []).filter(c => c.id !== itemId);
        break;
      case 'buzzer':
        updated.buzzerConfigs = (pos.buzzerConfigs || []).filter(b => b.id !== itemId);
        break;
    }

    this.editingEntity.set(updated);
  }

}
